import {
  ArrowTopRightOnSquareIcon,
  ChevronRightIcon,
  InformationCircleIcon,
  PhotoIcon,
  PlusIcon,
  TagIcon,
} from "@heroicons/react/24/outline";
import clsx from "clsx";
import { format, parseISO } from "date-fns";
import { DATE_DOT_FORMAT } from "../../../../constants";
import { getOrderStatusOption, type Order } from "../statusSelector/orderSteps";
import { Link, useNavigate } from "react-router-dom";
import { routeEndpoints } from "../../../../routes/routeEndpoints";

export default function OrderCard({ order }: { order: Order }) {
  const navigate = useNavigate();
  const statusOption = getOrderStatusOption(order.status);
  const StatusIcon = statusOption.icon;
  const formattedDate = format(parseISO(order.orderDate), DATE_DOT_FORMAT);

  return (
    <div className="flex flex-col gap-5 rounded-xl border border-gray-100 bg-white p-4 md:p-5">
      <div className="flex items-center gap-3 md:gap-8">
        <div className="flex size-14 md:size-20 shrink-0 items-center justify-center rounded-lg bg-gray-100">
          <PhotoIcon className="size-7 md:size-8 text-gray-400" />
        </div>
        <h3 className="flex-1 text-sm md:text-base font-medium text-gray-950">
          {order.title}
        </h3>
        <Link
          to={`${routeEndpoints.orderDetails}?id=${order.orderId}`}
          aria-label="View Details"
          className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 transition-colors cursor-pointer shrink-0"
        >
          <span className="hidden sm:inline">View Details</span>
          <ChevronRightIcon className="size-5" />
        </Link>
      </div>

      <div className="flex flex-col">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-y-4 rounded-xl bg-[#F9FAFB] px-4 py-4">
          <InfoItem label="Shipment Tracking:">
            {order.trackingNumber ? (
              <a
                href="#"
                onClick={(e) => e.preventDefault()}
                className="flex items-center gap-2 text-blue-600 hover:text-blue-700 min-w-0"
              >
                <span className="truncate">{order.trackingNumber}</span>
                <ArrowTopRightOnSquareIcon className="size-4 shrink-0" />
              </a>
            ) : (
              <span className="text-gray-400">—</span>
            )}
          </InfoItem>
          <InfoItem label="Order ID:">{order.orderId}</InfoItem>
          <InfoItem label="Order Date:">{formattedDate}</InfoItem>
          <InfoItem label="Order Status:">
            <span
              className={clsx(
                "inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs",
                statusOption.className,
              )}
            >
              {StatusIcon && <StatusIcon className="size-4" />}
              {statusOption.label}
            </span>
          </InfoItem>
        </div>

        {!order.trackingNumber && (
          <div className="mt-3 flex flex-col md:flex-row md:items-center justify-between gap-3 rounded-xl border border-[#F9814D]/40 bg-[#FFF9F0] px-4 py-3">
            <p className="flex items-center gap-2 text-xs md:text-sm text-[#F9814D]">
              <InformationCircleIcon className="size-4 shrink-0" />
              No shipping label or tracking ID for this order yet.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 md:gap-5">
              <button
                type="button"
                className="flex items-center justify-center gap-2 rounded-lg border border-blue-600 bg-white px-5 py-2 text-sm font-medium text-blue-600 hover:bg-blue-50 transition-colors cursor-pointer"
              >
                <PlusIcon className="size-5" />
                Add Tracking ID
              </button>
              <button
                type="button"
                onClick={() =>
                  navigate(
                    `${routeEndpoints.shippingLabel}?id=${order.orderId}`,
                  )
                }
                className="flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-5 py-2 text-sm font-medium text-white hover:bg-blue-700 transition-colors cursor-pointer"
              >
                <TagIcon className="size-5" />
                Buy Shipping Label
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const InfoItem = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <div className="flex flex-col gap-2 min-w-0 pr-4 text-sm">
    <span className="text-gray-500">{label}</span>
    <div className="text-gray-950">{children}</div>
  </div>
);
